type RoleVisibilityKey = "lmp" | "member";

type RoleVisibilityMap = Record<RoleVisibilityKey, readonly string[]>;

interface FilterSectionsByAllowedLabelsConfig<TSection, TItem> {
  getItems: (section: TSection) => readonly TItem[] | undefined;
  setItems: (section: TSection, items: TItem[]) => TSection;
  getItemLabel: (item: TItem) => string | undefined;
  getSectionLabel?: (section: TSection) => string | undefined;
}

/**
 * Normalizes a primary role value so formatting differences do not affect comparisons.
 * @param primaryRole - Raw role value from profile state.
 * @returns The normalized role token.
 */
export function normalizePrimaryRole(primaryRole: string): string {
  return primaryRole.trim().split(/[-_\s]+/).join("").toLowerCase();
}

/**
 * Returns the allowed label set for a role-based visibility map.
 * @param primaryRole - Raw role value from profile state.
 * @param visibilityMap - Allowed labels for known role keys.
 * @returns A set of allowed labels for supported roles, or null for unrestricted roles (Administrator only).
 */
export function getAllowedLabelsForPrimaryRole(
  primaryRole: string,
  visibilityMap: RoleVisibilityMap,
): Set<string> | null {
  const normalizedRole = normalizePrimaryRole(primaryRole);
  // Administrator gets unrestricted access.
  if (normalizedRole.includes("admin")) {
    return null;
  }
  if (normalizedRole === "lmp") {
    return new Set(visibilityMap.lmp);
  }
  // All other roles (Member, View-Only, Consultant, Coordinator, etc.)
  // see the member-restricted view.
  return new Set(visibilityMap.member);
}

/**
 * Filters grouped sections by allowed labels for either child items or top-level section labels.
 * @param sections - Source sections to evaluate.
 * @param allowedLabels - Allowed labels for the active role.
 * @param config - Accessors and mutator for section/item label extraction.
 * @returns A new filtered section array containing only allowed entries.
 */
export function filterSectionsByAllowedLabels<TSection, TItem>(
  sections: readonly TSection[],
  allowedLabels: Set<string>,
  config: FilterSectionsByAllowedLabelsConfig<TSection, TItem>,
): TSection[] {
  return sections
    .map((section) => {
      const items = config.getItems(section);
      if (!items) {
        const sectionLabel = config.getSectionLabel?.(section) ?? "";
        return allowedLabels.has(sectionLabel) ? section : null;
      }

      const filteredItems = items.filter((item) => {
        const itemLabel = config.getItemLabel(item) ?? "";
        return allowedLabels.has(itemLabel);
      });

      if (filteredItems.length === 0) {
        return null;
      }

      return config.setItems(section, filteredItems);
    })
    .filter((section): section is TSection => section !== null);
}
